import React, { useCallback, useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabaseClient'

// ── Types ──────────────────────────────────────────────

type Partner = {
  id: string
  name: string
  address: string | null
  logo_url: string | null
  website_url: string | null
  contact_name: string
  contact_email: string
  contact_phone: string | null
  status: 'ACTIVE' | 'DISABLED'
  color_code: string | null
  created_at: string
  updated_at: string
}

type PartnerMember = {
  id: string
  email: string
  full_name: string | null
  partner_role: 'ADMIN_PARTNER' | 'USER_PARTNER'
  is_active_partner: boolean
  created_at: string
}

type PartnerCoupon = {
  id: string
  code: string
  partner_id: string
  valid_from: string
  valid_until: string
  partner_commission_pct: number
  member_discount_pct: number
  max_usage_count: number | null
  current_usage_count: number
  is_active: boolean
  created_at: string
}

type CouponFormData = {
  code: string
  valid_from: string
  valid_until: string
  partner_commission_pct: number
  member_discount_pct: number
  max_usage_count: number | null
  is_active: boolean
}

type CreateAdminForm = {
  email: string
  password: string
  full_name: string
}

type Tab = 'info' | 'coupons' | 'members'

const EMPTY_COUPON_FORM: CouponFormData = {
  code: '',
  valid_from: '',
  valid_until: '',
  partner_commission_pct: 0,
  member_discount_pct: 0,
  max_usage_count: null,
  is_active: true,
}

const EMPTY_ADMIN_FORM: CreateAdminForm = {
  email: '',
  password: '',
  full_name: '',
}

// ── Helpers ────────────────────────────────────────────

const toInputDate = (iso: string) => iso?.slice(0, 10) ?? ''

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })

const validateCouponForm = (form: CouponFormData): string | null => {
  if (!form.code.trim()) return 'Le code est obligatoire.'
  if (!/^[A-Z0-9_-]{2,30}$/i.test(form.code.trim()))
    return 'Le code doit contenir 2-30 caractères alphanumériques (A-Z, 0-9, -, _).'
  if (!form.valid_from) return 'La date de début est obligatoire.'
  if (!form.valid_until) return 'La date de fin est obligatoire.'
  if (form.valid_from > form.valid_until)
    return 'La date de début doit être antérieure à la date de fin.'
  if (form.member_discount_pct < 0 || form.member_discount_pct > 100)
    return 'La réduction membre doit être entre 0 et 100%.'
  if (form.partner_commission_pct < 0 || form.partner_commission_pct > 100)
    return 'La commission partenaire doit être entre 0 et 100%.'
  return null
}

const validateAdminForm = (form: CreateAdminForm): string | null => {
  if (!form.email.trim()) return "L'email est obligatoire."
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return "L'email n'est pas valide."
  if (!form.password || form.password.length < 6) return 'Le mot de passe doit contenir au moins 6 caractères.'
  if (!form.full_name.trim()) return 'Le nom complet est obligatoire.'
  return null
}

// ── Styles ─────────────────────────────────────────────

const styles = {
  card: {
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '0.75rem',
    padding: '2rem',
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.9rem',
  } as React.CSSProperties,
  th: {
    textAlign: 'left' as const,
    padding: '0.75rem 0.5rem',
    borderBottom: '2px solid #e2e8f0',
    color: '#64748b',
    fontWeight: '600',
    fontSize: '0.85rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  } as React.CSSProperties,
  td: {
    padding: '0.75rem 0.5rem',
    borderBottom: '1px solid #f1f5f9',
    color: '#334155',
  } as React.CSSProperties,
  btnPrimary: {
    padding: '0.5rem 1rem',
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  btnSecondary: {
    padding: '0.4rem 0.75rem',
    background: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
  } as React.CSSProperties,
  btnDanger: {
    padding: '0.4rem 0.75rem',
    background: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
  } as React.CSSProperties,
  btnSuccess: {
    padding: '0.4rem 0.75rem',
    background: '#f0fdf4',
    color: '#16a34a',
    border: '1px solid #bbf7d0',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
  } as React.CSSProperties,
  badge: (active: boolean): React.CSSProperties => ({
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: '9999px',
    fontSize: '0.8rem',
    fontWeight: '500',
    background: active ? '#dcfce7' : '#fee2e2',
    color: active ? '#166534' : '#991b1b',
  }),
  roleBadge: (role: string): React.CSSProperties => ({
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: '9999px',
    fontSize: '0.8rem',
    fontWeight: '500',
    background: role === 'ADMIN_PARTNER' ? '#dbeafe' : '#f1f5f9',
    color: role === 'ADMIN_PARTNER' ? '#1e40af' : '#475569',
  }),
  tab: (active: boolean): React.CSSProperties => ({
    padding: '0.75rem 1.25rem',
    border: 'none',
    borderBottom: active ? '3px solid #3b82f6' : '3px solid transparent',
    background: 'transparent',
    cursor: 'pointer',
    fontWeight: active ? '600' : '400',
    color: active ? '#3b82f6' : '#64748b',
    fontSize: '0.95rem',
  }),
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  } as React.CSSProperties,
  modal: {
    background: 'white',
    borderRadius: '0.75rem',
    padding: '2rem',
    width: '100%',
    maxWidth: '560px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  } as React.CSSProperties,
  formGroup: { marginBottom: '1rem' } as React.CSSProperties,
  label: {
    display: 'block',
    marginBottom: '0.3rem',
    fontWeight: '500',
    fontSize: '0.9rem',
    color: '#334155',
  } as React.CSSProperties,
  input: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,
  select: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
    background: 'white',
  } as React.CSSProperties,
  infoRow: {
    display: 'flex',
    marginBottom: '0.75rem',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  infoLabel: {
    fontWeight: '600',
    minWidth: '150px',
    color: '#64748b',
  } as React.CSSProperties,
  infoValue: {
    color: '#334155',
  } as React.CSSProperties,
}

// ── Main Page ──────────────────────────────────────────

const AdminPartnerDetailPage: React.FC = () => {
  const { id: partnerId } = useParams<{ id: string }>()

  const [partner, setPartner] = useState<Partner | null>(null)
  const [members, setMembers] = useState<PartnerMember[]>([])
  const [coupons, setCoupons] = useState<PartnerCoupon[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>('info')

  // ── Coupon modal ──
  const [showCouponModal, setShowCouponModal] = useState(false)
  const [editingCoupon, setEditingCoupon] = useState<PartnerCoupon | null>(null)
  const [couponForm, setCouponForm] = useState<CouponFormData>(EMPTY_COUPON_FORM)
  const [couponSaving, setCouponSaving] = useState(false)
  const [couponError, setCouponError] = useState<string | null>(null)

  // ── Create admin member modal ──
  const [showAdminModal, setShowAdminModal] = useState(false)
  const [adminForm, setAdminForm] = useState<CreateAdminForm>(EMPTY_ADMIN_FORM)
  const [adminSaving, setAdminSaving] = useState(false)
  const [adminError, setAdminError] = useState<string | null>(null)
  const [adminSuccess, setAdminSuccess] = useState<string | null>(null)
  const [deletingMemberId, setDeletingMemberId] = useState<string | null>(null)

  // ── Load ──

  const load = useCallback(async () => {
    if (!partnerId) return
    setLoading(true)
    setError(null)

    const [{ data: pData, error: pErr }, { data: mData, error: mErr }, { data: cData, error: cErr }] =
      await Promise.all([
        supabase.from('partners').select('*').eq('id', partnerId).single<Partner>(),
        supabase
          .from('profiles')
          .select('id, email, full_name, partner_role, is_active_partner, created_at')
          .eq('partner_id', partnerId)
          .order('created_at'),
        supabase.from('coupons').select('*').eq('partner_id', partnerId).order('created_at', { ascending: false }),
      ])

    if (pErr || !pData) {
      setError('Partenaire introuvable.')
      setLoading(false)
      return
    }

    setPartner(pData)
    setMembers((mData as PartnerMember[]) ?? [])
    setCoupons((cData as PartnerCoupon[]) ?? [])
    setLoading(false)
  }, [partnerId])

  useEffect(() => {
    void load()
  }, [load])

  // ── Coupon CRUD ──

  const openCreateCoupon = () => {
    setEditingCoupon(null)
    setCouponForm(EMPTY_COUPON_FORM)
    setCouponError(null)
    setShowCouponModal(true)
  }

  const openEditCoupon = (coupon: PartnerCoupon) => {
    setEditingCoupon(coupon)
    setCouponForm({
      code: coupon.code,
      valid_from: toInputDate(coupon.valid_from),
      valid_until: toInputDate(coupon.valid_until),
      partner_commission_pct: coupon.partner_commission_pct,
      member_discount_pct: coupon.member_discount_pct,
      max_usage_count: coupon.max_usage_count,
      is_active: coupon.is_active,
    })
    setCouponError(null)
    setShowCouponModal(true)
  }

  const handleSaveCoupon = async () => {
    const err = validateCouponForm(couponForm)
    if (err) {
      setCouponError(err)
      return
    }

    setCouponSaving(true)
    setCouponError(null)

    const payload = {
      code: couponForm.code.trim().toUpperCase(),
      partner_id: partnerId!,
      valid_from: couponForm.valid_from,
      valid_until: couponForm.valid_until,
      partner_commission_pct: couponForm.partner_commission_pct,
      member_discount_pct: couponForm.member_discount_pct,
      max_usage_count: couponForm.max_usage_count,
      is_active: couponForm.is_active,
    }

    if (editingCoupon) {
      const { error: uErr } = await supabase
        .from('coupons')
        .update({ ...payload, updated_at: new Date().toISOString() })
        .eq('id', editingCoupon.id)

      if (uErr) {
        setCouponError(`Erreur: ${uErr.message}`)
        setCouponSaving(false)
        return
      }
    } else {
      const { error: iErr } = await supabase.from('coupons').insert(payload)
      if (iErr) {
        setCouponError(`Erreur: ${iErr.message}`)
        setCouponSaving(false)
        return
      }
    }

    setCouponSaving(false)
    setShowCouponModal(false)
    void load()
  }

  const handleToggleCoupon = async (coupon: PartnerCoupon) => {
    const { error: uErr } = await supabase
      .from('coupons')
      .update({ is_active: !coupon.is_active, updated_at: new Date().toISOString() })
      .eq('id', coupon.id)

    if (uErr) {
      alert(`Erreur: ${uErr.message}`)
      return
    }
    void load()
  }

  const handleDeleteCoupon = async (coupon: PartnerCoupon) => {
    if (coupon.current_usage_count > 0) {
      alert(`Impossible de supprimer ce coupon : il a été utilisé ${coupon.current_usage_count} fois.`)
      return
    }
    const ok = window.confirm(`Supprimer le coupon "${coupon.code}" ?`)
    if (!ok) return

    const { error: dErr } = await supabase.from('coupons').delete().eq('id', coupon.id)
    if (dErr) {
      alert(`Erreur: ${dErr.message}`)
      return
    }
    void load()
  }

  // ── Create Admin Partner Account ──

  const openCreateAdmin = () => {
    setAdminForm(EMPTY_ADMIN_FORM)
    setAdminError(null)
    setAdminSuccess(null)
    setShowAdminModal(true)
  }

  const handleCreateAdmin = async () => {
    const err = validateAdminForm(adminForm)
    if (err) {
      setAdminError(err)
      return
    }

    setAdminSaving(true)
    setAdminError(null)
    setAdminSuccess(null)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        setAdminError('Session expirée, reconnectez-vous.')
        setAdminSaving(false)
        return
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string

      const res = await fetch(`${supabaseUrl}/functions/v1/create-partner-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          email: adminForm.email.trim().toLowerCase(),
          password: adminForm.password,
          full_name: adminForm.full_name.trim(),
          partner_id: partnerId,
        }),
      })

      const data = await res.json()

      if (!res.ok) {
        setAdminError(data.error || `Erreur ${res.status}`)
        setAdminSaving(false)
        return
      }

      setAdminSuccess(`Compte créé pour ${adminForm.email}`)
      setAdminForm(EMPTY_ADMIN_FORM)
      void load()
    } catch (e: any) {
      setAdminError(`Erreur réseau: ${e.message}`)
    }

    setAdminSaving(false)
  }

  // ── Delete Partner Member ──

  const handleDeleteMember = async (member: PartnerMember) => {
    const confirmMsg = member.partner_role === 'ADMIN_PARTNER'
      ? `⚠️ Attention : "${member.full_name || member.email}" est un ADMIN du partenaire.\n\nSupprimer ce compte ? Cette action est irréversible.`
      : `Supprimer le membre "${member.full_name || member.email}" ?\n\nCette action est irréversible.`

    if (!window.confirm(confirmMsg)) return

    setDeletingMemberId(member.id)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        alert('Session expirée, reconnectez-vous.')
        setDeletingMemberId(null)
        return
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string

      const res = await fetch(`${supabaseUrl}/functions/v1/delete-partner-member`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ member_id: member.id }),
      })

      const data = await res.json()

      if (!res.ok) {
        alert(data.error || `Erreur ${res.status}`)
        setDeletingMemberId(null)
        return
      }

      void load()
    } catch (e: any) {
      alert(`Erreur réseau : ${e.message}`)
    }

    setDeletingMemberId(null)
  }

  // ── Render ──

  if (loading) {
    return (
      <div style={styles.card}>
        <p>Chargement…</p>
      </div>
    )
  }

  if (error || !partner) {
    return (
      <div style={styles.card}>
        <p style={{ color: '#dc2626' }}>{error || 'Partenaire introuvable.'}</p>
        <Link to="/admin/partners">← Retour aux partenaires</Link>
      </div>
    )
  }

  const hasAdmin = members.some(m => m.partner_role === 'ADMIN_PARTNER')

  return (
    <div data-testid="admin-partner-detail-page">
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <Link to="/admin/partners" style={{ color: '#64748b', textDecoration: 'none', fontSize: '0.9rem' }}>
          ← Retour aux partenaires
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
          <h1 style={{ margin: 0 }} data-testid="partner-detail-name">{partner.name}</h1>
          <span style={styles.badge(partner.status === 'ACTIVE')}>
            {partner.status === 'ACTIVE' ? 'Actif' : 'Désactivé'}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid #e2e8f0', marginBottom: '1.5rem' }}>
        <button style={styles.tab(activeTab === 'info')} onClick={() => setActiveTab('info')} data-testid="tab-info">
          📋 Informations
        </button>
        <button style={styles.tab(activeTab === 'coupons')} onClick={() => setActiveTab('coupons')} data-testid="tab-coupons">
          🎟️ Coupons ({coupons.length})
        </button>
        <button style={styles.tab(activeTab === 'members')} onClick={() => setActiveTab('members')} data-testid="tab-members">
          👥 Membres ({members.length})
        </button>
      </div>

      {/* ── Tab: Info ── */}
      {activeTab === 'info' && (
        <div style={styles.card} data-testid="tab-info-content">
          <h2 style={{ marginTop: 0 }}>Informations du partenaire</h2>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Nom</span>
            <span style={styles.infoValue}>{partner.name}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Adresse</span>
            <span style={styles.infoValue}>{partner.address || '—'}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Contact</span>
            <span style={styles.infoValue}>
              {partner.contact_name} — {partner.contact_email}
              {partner.contact_phone ? ` — ${partner.contact_phone}` : ''}
            </span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Site web</span>
            <span style={styles.infoValue}>
              {partner.website_url ? (
                <a href={partner.website_url} target="_blank" rel="noopener noreferrer">
                  {partner.website_url}
                </a>
              ) : (
                '—'
              )}
            </span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Couleur</span>
            <span style={styles.infoValue}>
              <span
                style={{
                  display: 'inline-block',
                  width: '1rem',
                  height: '1rem',
                  borderRadius: '50%',
                  background: partner.color_code ?? '#ccc',
                  border: '1px solid #e2e8f0',
                  verticalAlign: 'middle',
                  marginRight: '0.5rem',
                }}
              />
              {partner.color_code ?? '—'}
            </span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Créé le</span>
            <span style={styles.infoValue}>{formatDate(partner.created_at)}</span>
          </div>
        </div>
      )}

      {/* ── Tab: Coupons ── */}
      {activeTab === 'coupons' && (
        <div data-testid="tab-coupons-content">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <button style={styles.btnPrimary} onClick={openCreateCoupon} data-testid="create-coupon-btn">
              + Nouveau coupon
            </button>
          </div>

          <div style={styles.card}>
            {coupons.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#94a3b8' }} data-testid="no-coupons">
                Aucun coupon pour ce partenaire.
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={styles.table} data-testid="coupons-table">
                  <thead>
                    <tr>
                      <th style={styles.th}>Code</th>
                      <th style={styles.th}>Validité</th>
                      <th style={styles.th}>Réduction</th>
                      <th style={styles.th}>Commission</th>
                      <th style={styles.th}>Utilisations</th>
                      <th style={styles.th}>Statut</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coupons.map(coupon => (
                      <tr key={coupon.id} data-testid={`coupon-row-${coupon.id}`}>
                        <td style={styles.td}>
                          <code style={{ background: '#f1f5f9', padding: '0.2rem 0.4rem', borderRadius: '0.25rem' }}>
                            {coupon.code}
                          </code>
                        </td>
                        <td style={styles.td}>
                          {formatDate(coupon.valid_from)} → {formatDate(coupon.valid_until)}
                        </td>
                        <td style={styles.td}>{coupon.member_discount_pct}%</td>
                        <td style={styles.td}>{coupon.partner_commission_pct}%</td>
                        <td style={styles.td}>
                          {coupon.current_usage_count}
                          {coupon.max_usage_count !== null ? ` / ${coupon.max_usage_count}` : ' / ∞'}
                        </td>
                        <td style={styles.td}>
                          <span style={styles.badge(coupon.is_active)}>
                            {coupon.is_active ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              style={styles.btnSecondary}
                              onClick={() => openEditCoupon(coupon)}
                              data-testid={`coupon-edit-btn-${coupon.id}`}
                            >
                              ✏️
                            </button>
                            <button
                              style={coupon.is_active ? styles.btnDanger : styles.btnSuccess}
                              onClick={() => handleToggleCoupon(coupon)}
                              data-testid={`coupon-toggle-btn-${coupon.id}`}
                            >
                              {coupon.is_active ? 'Désactiver' : 'Activer'}
                            </button>
                            <button
                              style={styles.btnDanger}
                              onClick={() => handleDeleteCoupon(coupon)}
                              data-testid={`coupon-delete-btn-${coupon.id}`}
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Members ── */}
      {activeTab === 'members' && (
        <div data-testid="tab-members-content">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem', gap: '0.5rem' }}>
            {!hasAdmin && (
              <button style={styles.btnPrimary} onClick={openCreateAdmin} data-testid="create-admin-btn">
                + Créer le 1er admin
              </button>
            )}
            {hasAdmin && (
              <p style={{ flex: 1, color: '#64748b', fontSize: '0.85rem', margin: 0, alignSelf: 'center' }}>
                ✅ Un admin partenaire existe. Les membres supplémentaires peuvent être créés depuis le portail partenaire.
              </p>
            )}
          </div>

          <div style={styles.card}>
            {members.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem' }} data-testid="no-members">
                <p style={{ color: '#94a3b8', marginBottom: '1rem' }}>
                  Aucun membre pour ce partenaire.
                </p>
                <p style={{ color: '#64748b', fontSize: '0.85rem' }}>
                  Créez le premier compte administrateur pour permettre au partenaire de se connecter au portail.
                </p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={styles.table} data-testid="members-table">
                  <thead>
                    <tr>
                      <th style={styles.th}>Nom</th>
                      <th style={styles.th}>Email</th>
                      <th style={styles.th}>Rôle</th>
                      <th style={styles.th}>Statut</th>
                      <th style={styles.th}>Créé le</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map(member => (
                      <tr key={member.id} data-testid={`member-row-${member.id}`}>
                        <td style={styles.td}>{member.full_name || '—'}</td>
                        <td style={styles.td}>{member.email}</td>
                        <td style={styles.td}>
                          <span style={styles.roleBadge(member.partner_role)}>
                            {member.partner_role === 'ADMIN_PARTNER' ? 'Admin' : 'Membre'}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span style={styles.badge(member.is_active_partner !== false)}>
                            {member.is_active_partner !== false ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td style={styles.td}>{formatDate(member.created_at)}</td>
                        <td style={styles.td}>
                          <button
                            style={styles.btnDanger}
                            onClick={() => handleDeleteMember(member)}
                            disabled={deletingMemberId === member.id}
                            data-testid={`member-delete-btn-${member.id}`}
                          >
                            {deletingMemberId === member.id ? 'Suppression…' : '🗑️ Supprimer'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Coupon Modal ── */}
      {showCouponModal && (
        <div style={styles.overlay} onClick={() => setShowCouponModal(false)} data-testid="coupon-modal">
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, marginBottom: '1.5rem' }}>
              {editingCoupon ? `Modifier "${editingCoupon.code}"` : 'Nouveau coupon'}
            </h2>

            {couponError && (
              <div
                style={{ padding: '0.75rem', background: '#fef2f2', color: '#dc2626', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.9rem' }}
                data-testid="coupon-modal-error"
              >
                {couponError}
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Code *</label>
              <input
                style={styles.input}
                data-testid="coupon-code-input"
                value={couponForm.code}
                onChange={e => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                placeholder="CLUB2026"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Début *</label>
                <input
                  style={styles.input}
                  type="date"
                  data-testid="coupon-valid-from-input"
                  value={couponForm.valid_from}
                  onChange={e => setCouponForm({ ...couponForm, valid_from: e.target.value })}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Fin *</label>
                <input
                  style={styles.input}
                  type="date"
                  data-testid="coupon-valid-until-input"
                  value={couponForm.valid_until}
                  onChange={e => setCouponForm({ ...couponForm, valid_until: e.target.value })}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Réduction membre (%)</label>
                <input
                  style={styles.input}
                  type="number"
                  min="0"
                  max="100"
                  data-testid="coupon-discount-input"
                  value={couponForm.member_discount_pct}
                  onChange={e => setCouponForm({ ...couponForm, member_discount_pct: Number(e.target.value) })}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Commission partenaire (%)</label>
                <input
                  style={styles.input}
                  type="number"
                  min="0"
                  max="100"
                  data-testid="coupon-commission-input"
                  value={couponForm.partner_commission_pct}
                  onChange={e => setCouponForm({ ...couponForm, partner_commission_pct: Number(e.target.value) })}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Max utilisations</label>
                <input
                  style={styles.input}
                  type="number"
                  min="0"
                  data-testid="coupon-max-usage-input"
                  value={couponForm.max_usage_count ?? ''}
                  onChange={e =>
                    setCouponForm({
                      ...couponForm,
                      max_usage_count: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                  placeholder="Illimité"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Statut</label>
                <select
                  style={styles.select}
                  data-testid="coupon-status-select"
                  value={couponForm.is_active ? 'true' : 'false'}
                  onChange={e => setCouponForm({ ...couponForm, is_active: e.target.value === 'true' })}
                >
                  <option value="true">Actif</option>
                  <option value="false">Inactif</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button style={styles.btnSecondary} onClick={() => setShowCouponModal(false)} data-testid="coupon-modal-cancel">
                Annuler
              </button>
              <button style={styles.btnPrimary} onClick={handleSaveCoupon} disabled={couponSaving} data-testid="coupon-modal-save">
                {couponSaving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Admin Modal ── */}
      {showAdminModal && (
        <div style={styles.overlay} onClick={() => setShowAdminModal(false)} data-testid="admin-modal">
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h2 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Créer le 1er compte admin</h2>
            <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: 0, marginBottom: '1.5rem' }}>
              Ce compte sera le premier administrateur du portail partenaire pour «&nbsp;{partner.name}&nbsp;».
              Il pourra ensuite créer d'autres membres depuis le portail.
            </p>

            {adminError && (
              <div
                style={{ padding: '0.75rem', background: '#fef2f2', color: '#dc2626', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.9rem' }}
                data-testid="admin-modal-error"
              >
                {adminError}
              </div>
            )}

            {adminSuccess && (
              <div
                style={{ padding: '0.75rem', background: '#f0fdf4', color: '#16a34a', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.9rem' }}
                data-testid="admin-modal-success"
              >
                {adminSuccess}
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Nom complet *</label>
              <input
                style={styles.input}
                data-testid="admin-fullname-input"
                value={adminForm.full_name}
                onChange={e => setAdminForm({ ...adminForm, full_name: e.target.value })}
                placeholder="Jean Dupont"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Email *</label>
              <input
                style={styles.input}
                type="email"
                data-testid="admin-email-input"
                value={adminForm.email}
                onChange={e => setAdminForm({ ...adminForm, email: e.target.value })}
                placeholder="admin@club.ch"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Mot de passe *</label>
              <input
                style={styles.input}
                type="password"
                data-testid="admin-password-input"
                value={adminForm.password}
                onChange={e => setAdminForm({ ...adminForm, password: e.target.value })}
                placeholder="Min. 6 caractères"
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
              <button style={styles.btnSecondary} onClick={() => setShowAdminModal(false)} data-testid="admin-modal-cancel">
                Annuler
              </button>
              <button
                style={styles.btnPrimary}
                onClick={handleCreateAdmin}
                disabled={adminSaving || !!adminSuccess}
                data-testid="admin-modal-save"
              >
                {adminSaving ? 'Création…' : 'Créer le compte'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminPartnerDetailPage
